import { Badge, Grid } from '@material-ui/core';
import React from 'react';
import { isEqual } from 'lodash';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { getProductListingPrice } from '../../../common/helpers/price-formatter';
import VerticalCenter from '../../../vertical-center/vertical-center';
import { styles } from './product-list-styles';
import { useTranslation } from 'react-i18next';

interface IProductsListProps {
  listings: IItemLookup[];
  selectedListing?: IItemLookup;
  onClick: (listing: IItemLookup) => void;
}

const ProductList: React.FC<IProductsListProps> = ({
  listings,
  selectedListing,
  onClick,
}: IProductsListProps): JSX.Element | null => {
  const classes = styles();
  return (
    <Grid
      container
      direction='column'
      className={classes.component}
      data-testid='search-product-results'
    >
      <Grid item className={`fullHeight`}>
        {listings.map(
          (listing, index): JSX.Element => {
            const selected: boolean =
              !!selectedListing && listing.itemId === selectedListing.itemId;
            return (
              <ProductListing
                key={`${listing.itemId}_${index}`}
                listing={listing}
                selected={selected}
                onClick={onClick}
              />
            );
          },
        )}
      </Grid>
    </Grid>
  );
};

// TODO: BM - At small screen resolutions when the names will overlap, need to determine how we handle it? Ellipsis?

interface IProductListingProps {
  listing: IItemLookup;
  selected: boolean;
  onClick: (listing: IItemLookup) => void;
}

export const ProductListing = (props: IProductListingProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  const { listing, selected, onClick } = props;
  const { itemId } = listing;
  const className = `result ${selected ? `${classes.selected}` : ''}`;
  return (
    <Grid
      direction='column'
      spacing={0}
      wrap='nowrap'
      container
      onClick={() => onClick(listing)}
      className={`${classes.itemContainer} ${className}`}
      data-testid={`search-result${selected ? '-selected' : ''}`}
    >
      {/* Main part of search result */}
      <Grid item className='fullHeight'>
        <Badge
          style={{ width: '100%' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          showZero={true}
          invisible={!selected}
          className='fullHeight'
        >
          <Grid container justifyContent='flex-start'>
            <Grid item xs={6} className={classes.productNameContainer}>
              <VerticalCenter
                containerClass={classes.productNameContainer}
                itemClass={classes.fullWidth}
              >
                <ProductListingName {...listing} />
              </VerticalCenter>
            </Grid>
            <Grid item xs={4} className={classes.priceContainer}>
              <VerticalCenter
                containerClass={classes.priceContainer}
                itemClass={classes.productInformation}
              >
                {itemId}
              </VerticalCenter>
            </Grid>
            <Grid item xs={2} className={classes.priceContainer} data-testid='search-item-price'>
              <VerticalCenter
                containerClass={classes.priceContainer}
                itemClass={classes.priceInformation}
              >
                {t(getProductListingPrice(listing))}
              </VerticalCenter>
            </Grid>
          </Grid>
        </Badge>
      </Grid>
    </Grid>
  );
};

const ProductListingName = (listing: IItemLookup): JSX.Element => {
  const classes = styles();
  const { description } = listing;
  return (
    <Grid container direction='column'>
      {description && (
        <Grid
          item
          className={classes.productInformation}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </Grid>
  );
};
const shouldNotReRender = (prevProps, nextProps) => {
  const isRender =
    !isEqual(prevProps.listings, nextProps.listings) ||
    prevProps.selectedListing !== nextProps.selectedListing;
  return !isRender;
};
export default React.memo(ProductList, shouldNotReRender);
